import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs/operators';
import { ToastService } from '@shared/components/toast/services/toast.service';
import {
  SHOW_SUCCESS_TOAST_ON_GET,
  SUPPRESS_SUCCESS_TOAST,
  TOAST_DEFAULT_METHODS,
  extractEnvelopeMessage,
} from '@core/http/api-response.envelope';

/**
 * Surface server-localized success messages globally.
 *
 * For every response in the 2xx range that carries the standard envelope
 * (`{ statusCode, message, data, ... }`), this interceptor calls
 * `toastService.success(envelope.message)` so individual features no longer
 * need to plumb the message through actions → reducers → tap-effects.
 *
 * Defaults:
 * - `POST` / `PUT` / `PATCH` / `DELETE` → toast on success
 * - `GET` / `HEAD` / `OPTIONS`         → silent (would otherwise flood the
 *   UI on every page load)
 *
 * Opt-outs / opt-ins (per request, via `HttpContext`):
 * - `withSuppressSuccessToast()` — disable for a specific call
 * - `withShowSuccessToastOnGet()` — enable for a specific GET
 *
 * Errors are handled by `errorInterceptor`; this interceptor never sees
 * them because `tap` only forwards `next` events.
 */
export const apiResponseInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);

  return next(req).pipe(
    tap(event => {
      if (!(event instanceof HttpResponse)) return;
      if (event.status < 200 || event.status >= 300) return;

      if (req.context.get(SUPPRESS_SUCCESS_TOAST)) return;

      const isMutation = TOAST_DEFAULT_METHODS.has(req.method.toUpperCase());
      const showOnGet = req.context.get(SHOW_SUCCESS_TOAST_ON_GET);
      if (!isMutation && !showOnGet) return;

      const message = extractEnvelopeMessage(event.body);
      if (message) {
        toastService.success(message, 3000);
      }
    })
  );
};
